import React, { useState, useRef, useCallback, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { Paginator } from 'primereact/paginator';
import { Link } from 'react-router-dom';
import agent from '../../../agent';

const AGENT_OPTIONS = [
    { label: 'Все', value: '' },
    { label: 'SkStore', value: 'SKSTORE' },
];

const PAGE_SIZE_OPTIONS = [
    { label: '10', value: 10 },
    { label: '20', value: 20 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
];

const toApiDate = (date) => {
    if (!date) return undefined;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const formatAmount = (num) => {
    if (num == null) return '—';
    return Number(num).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDateTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    const day = String(d.getDate()).padStart(2, '0');
    const mon = String(d.getMonth() + 1).padStart(2, '0');
    const hrs = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${day}.${mon}.${d.getFullYear()} ${hrs}:${min}`;
};

const AgentReward = () => {
    const [rows, setRows] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [loading, setLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [selectedAgent, setSelectedAgent] = useState('SKSTORE');

    const activeFilters = useRef({});
    const toast = useRef(null);

    const buildParams = useCallback((df, dt) => {
        const params = {};
        const from = toApiDate(df);
        const to = toApiDate(dt);
        if (from) params.dateFrom = from;
        if (to) params.dateTo = to;
        return params;
    }, []);

    const loadPage = useCallback((p, ps, filters) => {
        setLoading(true);
        agent.AgentReward.getPage({ ...filters, page: p, itemsPerPage: ps })
            .then(res => {
                setRows(res?.content ?? []);
                setTotalElements(res?.totalElements ?? 0);
            })
            .catch(() => {
                toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось загрузить данные', life: 3000 });
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadPage(0, 20, {});
    }, [loadPage]);

    const handleSearch = () => {
        const filters = buildParams(dateFrom, dateTo);
        activeFilters.current = filters;
        setPage(0);
        loadPage(0, pageSize, filters);
    };

    const handleReset = () => {
        setDateFrom(null);
        setDateTo(null);
        setSelectedAgent('SKSTORE');
        activeFilters.current = {};
        setPage(0);
        loadPage(0, pageSize, {});
    };

    const handleExcel = () => {
        const filters = buildParams(dateFrom, dateTo);
        setExcelLoading(true);
        agent.AgentReward.getExcel(filters)
            .then(res => {
                const blob = res?.body;
                if (!blob) return;
                const disposition = res?.headers?.['content-disposition'] ?? '';
                const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                const filename = match ? match[1].replace(/['"]/g, '') : 'agent-reward.xlsx';
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            })
            .catch(() => {
                toast.current?.show({ severity: 'error', summary: 'Ошибка', detail: 'Не удалось выгрузить Excel', life: 3000 });
            })
            .finally(() => setExcelLoading(false));
    };

    const onPageChange = (e) => {
        setPage(e.page);
        setPageSize(e.rows);
        loadPage(e.page, e.rows, activeFilters.current);
    };

    const orderNumBody = (row) => (
        <Link to={`/order/${row.orderNum}`} style={{ color: '#2196F3', textDecoration: 'none' }}>
            {row.orderNum}
        </Link>
    );

    const amountBody = (field) => (row) => (
        <span style={{ display: 'block', textAlign: 'right' }}>{formatAmount(row[field])}</span>
    );

    const percentBody = (row) => (
        <span>{row.rewardPercent != null ? `${row.rewardPercent}%` : '—'}</span>
    );

    const dateBody = (row) => formatDateTime(row.deliveredAt);

    const indexBody = (row, opts) => opts.rowIndex + 1 + page * pageSize;

    return (
        <div style={{ padding: '24px' }}>
            <Toast ref={toast} />
            <h2 style={{ marginBottom: '20px' }}>Реестр начислений агентского вознаграждения</h2>

            <div style={{
                background: '#fff',
                border: '1px solid #dee2e6',
                borderRadius: '6px',
                padding: '16px 20px',
                marginBottom: '20px',
            }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-end' }}>
                        <div>
                            <div style={{ marginBottom: '6px', fontSize: '13px', color: '#555' }}>Дата доставки</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Calendar
                                    value={dateFrom}
                                    onChange={e => setDateFrom(e.value)}
                                    dateFormat="dd.mm.yy"
                                    placeholder="дд.мм.гггг"
                                    showIcon
                                    style={{ width: '200px' }}
                                />
                                <span style={{ color: '#888' }}>—</span>
                                <Calendar
                                    value={dateTo}
                                    onChange={e => setDateTo(e.value)}
                                    dateFormat="dd.mm.yy"
                                    placeholder="дд.мм.гггг"
                                    showIcon
                                    style={{ width: '200px' }}
                                />
                            </div>
                        </div>

                        <div>
                            <div style={{ marginBottom: '6px', fontSize: '13px', color: '#555' }}>Агент</div>
                            <Dropdown
                                value={selectedAgent}
                                options={AGENT_OPTIONS}
                                onChange={e => setSelectedAgent(e.value)}
                                style={{ width: '180px' }}
                            />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                        <Button label="Найти" icon="pi pi-search" onClick={handleSearch} />
                        <Button label="Сбросить" icon="pi pi-refresh" className="p-button-outlined p-button-secondary" onClick={handleReset} />
                        <Button
                            label="Выгрузить Excel"
                            icon="pi pi-file-excel"
                            className="p-button-outlined"
                            loading={excelLoading}
                            onClick={handleExcel}
                        />
                    </div>
                </div>
            </div>

            <DataTable
                value={rows}
                loading={loading}
                emptyMessage="Нет данных"
                stripedRows
                style={{ marginBottom: '8px' }}
            >
                <Column header="№" body={indexBody} style={{ width: '60px' }} />
                <Column header="Номер заказа" body={orderNumBody} style={{ minWidth: '140px' }} />
                <Column header="Агент" field="source" style={{ minWidth: '100px' }} />
                <Column header="ФИО клиента" field="clientFullName" body={r => r.clientFullName || '—'} style={{ minWidth: '200px' }} />
                <Column header="ИИН" field="clientIin" body={r => r.clientIin || '—'} style={{ minWidth: '130px' }} />
                <Column header="Сумма заказа (₸)" body={amountBody('orderAmount')} style={{ minWidth: '140px' }} />
                <Column header="%" body={percentBody} style={{ width: '70px' }} />
                <Column header="Вознаграждение (₸)" body={amountBody('rewardAmount')} style={{ minWidth: '150px' }} />
                <Column header="Дата доставки" body={dateBody} style={{ minWidth: '160px' }} />
            </DataTable>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#555' }}>
                    Показать на странице:
                    <Dropdown
                        value={pageSize}
                        options={PAGE_SIZE_OPTIONS}
                        onChange={e => {
                            setPageSize(e.value);
                            setPage(0);
                            loadPage(0, e.value, activeFilters.current);
                        }}
                        style={{ width: '80px' }}
                    />
                </div>
                <Paginator
                    first={page * pageSize}
                    rows={pageSize}
                    totalRecords={totalElements}
                    onPageChange={onPageChange}
                    template="PrevPageLink PageLinks NextPageLink"
                    style={{ background: 'transparent', border: 'none', padding: 0 }}
                />
            </div>
        </div>
    );
};

export default AgentReward;
